import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { regionUrl, type ReviewItemRow, type ReviewStats, type ReviewSuggestion } from '../../lib/api'

/**
 * The pieces of the editor's queue.
 *
 * The screen is built for a student worker with both hands on the keyboard and
 * no mouse: crop on the left, recognised text on the right, one decision, next
 * item. Every part here is therefore addressable by key, and the mouse targets
 * exist only because a mouse is sometimes what a person has.
 */

/** A page label and reading order, as the rail and the header print them. */
export function itemLabel(item: ReviewItemRow): string {
  const order = item.reading_order
  return order === null || order === undefined
    ? (item.page_label ?? 'no page')
    : `${item.page_label ?? 'no page'} · R${String(order).padStart(2, '0')}`
}

/** The queue ahead, lowest confidence first, the current item outlined in the accent. */
export function UpNextRail({
  items,
  index,
  total,
  onPick,
}: {
  items: ReviewItemRow[]
  index: number
  total: number
  onPick: (next: number) => void
}) {
  return (
    <div className="flex w-[200px] flex-none flex-col border-r border-n300 bg-surface">
      <div className="flex flex-none items-baseline justify-between border-b border-n300 px-[13px] pb-[9px] pt-[11px]">
        <div className="chrome text-[11px]">Up next</div>
        <div className="font-mono text-[10px] text-ink2">{total.toLocaleString()}</div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-[5px] overflow-y-auto px-[9px] py-2">
        {items.map((item, position) => {
          const current = position === index
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onPick(position)}
              aria-current={current ? 'true' : undefined}
              className="flex items-center gap-2 rounded-[--radius-md] px-2 py-[5px] text-left"
              style={{
                background: current ? 'var(--color-bg)' : 'transparent',
                boxShadow: current ? 'inset 0 0 0 1px var(--jl-accent)' : 'none',
              }}
            >
              <span className="min-w-0 flex-1 truncate font-mono text-[11px]">
                {itemLabel(item)}
              </span>
              <span className="flex-none font-mono text-[10.5px] text-mark">
                {item.confidence === null || item.confidence === undefined
                  ? '-'
                  : `${(item.confidence * 100).toFixed(1)}%`}
              </span>
            </button>
          )
        })}
      </div>
      <div className="flex-none border-t border-n300 px-[13px] py-2.5 font-mono text-[9.5px] uppercase leading-[1.75] text-ink2">
        <div className="text-ink">Sorted by confidence, lowest first</div>
        <div>Same edition batched together</div>
      </div>
    </div>
  )
}

/**
 * The crop, on the dark well.
 *
 * The well stays near-black in both themes and the scan is never dimmed,
 * inverted or filtered: it is evidence, and this interface has no licence to
 * retouch it.
 */
export function CropPanel({
  item,
  zoom,
  onZoom,
}: {
  item: ReviewItemRow
  zoom: number
  onZoom: (next: number) => void
}) {
  const region = item.region
  const width = region ? Math.round(region[2] - region[0]) : null
  const height = region ? Math.round(region[3] - region[1]) : null
  // The API computes the crop's URL where it can, because it knows the page's
  // raster space; the client only falls back to building one itself.
  const served = (item as ReviewItemRow & { crop_url?: string | null }).crop_url
  const source =
    served ??
    (item.page_id && region
      ? regionUrl(item.page_id, region, Math.round((width ?? 300) * 1.6))
      : null)

  /*
   * Zoom is a multiple of the fit, not of the raster.
   *
   * A queue item is anything from a four-word headline to a whole column, so a
   * fixed magnification shows one of them and clips the other. The crop
   * arrives fitted to the well, and the label reports the magnification that
   * turns out to be, because the figure is what an editor writes down.
   */
  const well = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState({ width: 520, height: 520 })
  useEffect(() => {
    const node = well.current
    if (!node) return
    const measure = () =>
      setBox({ width: node.clientWidth - 40, height: node.clientHeight - 40 })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const fit =
    width && height
      ? Math.max(80, Math.min(box.width, (box.height * width) / height, width * 4))
      : 0
  const shown = Math.round(fit * (zoom / 100))
  const magnification = width ? Math.round((shown / width) * 100) : 0

  return (
    <div
      ref={well}
      className="relative flex min-h-0 items-center justify-center overflow-auto border-r border-rule2 bg-well p-5"
    >
      {source && width && height ? (
        <img
          src={source}
          alt={`The crop under review: ${item.pub_date ?? 'undated'}, page ${item.page_label ?? '?'}`}
          style={{ width: shown, maxWidth: 'none' }}
          className="block shadow-[0_0_0_1px_#4f4b48]"
        />
      ) : (
        <div className="max-w-[280px] text-center font-mono text-[10px] leading-[1.7] text-[#bdb6a7]">
          NO CROP FOR THIS ITEM.
          <br />
          {item.page_id
            ? 'THE ITEM CARRIES NO REGION, SO THERE IS NOTHING TO CUT OUT OF THE PAGE.'
            : 'THE ITEM IS NOT BOUND TO A PAGE, SO THE PAGE IMAGE CANNOT BE ADDRESSED.'}
        </div>
      )}

      <div
        className="absolute left-[14px] top-[14px] rounded-[--radius-md] px-[7px] py-[3px] font-mono text-[9.5px] uppercase tracking-[0.1em]"
        style={{ background: 'rgba(26,25,24,0.7)', color: '#bdb6a7' }}
      >
        {region
          ? `Crop at ${magnification}% · ${Math.round(region[0])}, ${Math.round(region[1])}, ${width} × ${height} px`
          : 'No region recorded'}
      </div>

      <div className="absolute bottom-[14px] right-[14px] flex gap-[5px]">
        <WellButton label="Zoom out" onClick={() => onZoom(Math.max(25, zoom - 25))}>
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2 6h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </WellButton>
        <WellButton label="Zoom in" onClick={() => onZoom(Math.min(800, zoom + 25))}>
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2 6h8M6 2v8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </WellButton>
        {item.page_id && region ? (
          <Link
            to={`/page/${item.page_id}?r=${Math.round(region[0])},${Math.round(region[1])},${width},${height}`}
            aria-label="Open this region in the page viewer"
            title="Open this region in the page viewer"
            className="flex h-[30px] w-[30px] items-center justify-center rounded-[--radius-md] border no-underline"
            style={{ background: 'rgba(26,25,24,0.86)', borderColor: '#4f4b48', color: '#e6e2e0' }}
          >
            <ExpandGlyph />
          </Link>
        ) : null}
      </div>
    </div>
  )
}

function WellButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-[30px] w-[30px] items-center justify-center rounded-[--radius-md] border"
      style={{ background: 'rgba(26,25,24,0.86)', borderColor: '#4f4b48', color: '#e6e2e0' }}
    >
      {children}
    </button>
  )
}

/** Drawn, because the vendored subsets carry no U+2922. */
function ExpandGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M7 1.5h3.5V5M5 10.5H1.5V7M10.5 1.5 7 5M1.5 10.5 5 7"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * The recognised text, with the tokens the recogniser doubts.
 *
 * A doubtful token takes a dotted magenta underline and nothing else: the
 * letterforms are not altered, so an editor reading for a correction sees
 * exactly what the recognition produced. The one selected token is boxed in
 * the accent, which is the accent doing its "the thing you chose" job.
 */

/** A word stripped of the punctuation that is not part of the reading. */
function bare(word: string): string {
  return word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').toLowerCase()
}

/**
 * Where each doubtful token sits in the text.
 *
 * The queue's tokens are numbered among themselves rather than among the
 * words of the block, so a token's `index` is not a position in `raw_text`.
 * The tokens are matched back onto the words by their text, in order, which is
 * what makes the underline land on the word the recogniser actually doubted
 * rather than on the fourth word of the paragraph.
 */
export function markedWords(item: ReviewItemRow): {
  pieces: { piece: string; position: number | null }[]
  marks: Map<number, { text: string; confidence: number | null }>
} {
  const text = item.raw_text ?? item.tokens.map((token) => token.text).join(' ')
  const pieces: { piece: string; position: number | null }[] = []
  const marks = new Map<number, { text: string; confidence: number | null }>()
  if (!text.trim()) return { pieces, marks }

  const pending = item.tokens.filter((token) => token.flagged && token.text)
  const remaining = new Map<string, (typeof pending)[number]>()
  for (const token of pending) remaining.set(bare(token.text), token)

  let cursor = 0
  for (const [position, piece] of text.split(/(\s+)/).entries()) {
    if (/^\s+$/.test(piece) || piece === '') {
      pieces.push({ piece, position: null })
      continue
    }
    pieces.push({ piece, position })
    const word = bare(piece)
    if (!word) continue
    const next = pending[cursor]
    if (next && (word === bare(next.text) || word.includes(bare(next.text)))) {
      marks.set(position, { text: piece, confidence: next.confidence })
      cursor += 1
      continue
    }
    const loose = remaining.get(word)
    if (loose) marks.set(position, { text: piece, confidence: loose.confidence })
  }
  return { pieces, marks }
}

export function TokenText({
  item,
  selected,
  onSelect,
}: {
  item: ReviewItemRow
  selected: number | null
  onSelect: (position: number) => void
}) {
  const { pieces, marks } = useMemo(() => markedWords(item), [item])

  if (!pieces.length) {
    return (
      <p className="text-[15px] leading-normal text-ink2">
        This item carries no recognised text. It was queued on its geometry rather than on its
        words, so the decision is about the region on the left rather than about a spelling. The
        crop is the whole evidence.
      </p>
    )
  }

  return (
    <div className="text-[16.5px] leading-[1.7]">
      {pieces.map(({ piece, position }, key) => {
        const token = position === null ? undefined : marks.get(position)
        if (!token) return <span key={key}>{piece}</span>
        const isSelected = selected === position
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(position as number)}
            aria-current={isSelected ? 'true' : undefined}
            title={
              token.confidence === null
                ? 'The recogniser flagged this word'
                : `${(token.confidence * 100).toFixed(1)}% confident`
            }
            className="inline p-0 text-left align-baseline text-[16.5px] leading-[1.7]"
            style={{
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textDecorationColor: 'var(--color-accent-2-700)',
              textDecorationThickness: 1.5,
              textUnderlineOffset: 3,
              background: isSelected ? 'var(--jl-accent-100)' : 'transparent',
              boxShadow: isSelected ? '0 0 0 1.5px var(--jl-accent)' : 'none',
            }}
          >
            {piece}
          </button>
        )
      })}
    </div>
  )
}

/** The numbered suggestion rows. Row n is picked with the n key. */
export function Suggestions({
  suggestions,
  onPick,
  onFreeform,
}: {
  suggestions: ReviewSuggestion[]
  onPick: (suggestion: ReviewSuggestion) => void
  onFreeform: () => void
}) {
  const rows = suggestions.slice(0, 4)
  return (
    <div className="flex flex-col gap-px overflow-hidden rounded-[--radius-md] border border-n300 bg-n300">
      {rows.map((suggestion, index) => (
        <button
          key={`${suggestion.text}-${index}`}
          type="button"
          onClick={() => onPick(suggestion)}
          className="flex items-center gap-[11px] px-3 py-2.5 text-left"
          style={{ background: suggestion.default ? 'var(--jl-accent-100)' : 'var(--color-bg)' }}
        >
          <span
            className="w-[14px] flex-none font-mono text-[10px]"
            style={{ color: suggestion.default ? 'var(--jl-accent)' : 'var(--color-neutral-700)' }}
          >
            {index + 1}
          </span>
          <span className="w-[108px] flex-none font-mono text-[14px]">{suggestion.text}</span>
          <span className="flex-1 text-[13.5px] leading-[1.35] text-ink2">
            {suggestion.source}
            {suggestion.note ? ` · ${suggestion.note}` : ''}
          </span>
          {suggestion.default && (
            <span className="flex-none font-mono text-[9.5px] uppercase tracking-[0.08em] text-accent">
              Default
            </span>
          )}
        </button>
      ))}
      <button
        type="button"
        onClick={onFreeform}
        className="flex items-center gap-[11px] bg-paper px-3 py-2.5 text-left"
      >
        <span className="w-[14px] flex-none font-mono text-[10px] text-ink2">{rows.length + 1}</span>
        <span className="w-[108px] flex-none font-mono text-[14px]">type something else</span>
        <span className="flex-1 text-[13.5px] leading-[1.35] text-ink2">
          free entry, recorded as a manual correction
        </span>
      </button>
    </div>
  )
}

/** This session, the last fortnight, and what the queue is made of. */
export function SessionRail({ stats }: { stats: ReviewStats | undefined }) {
  const session = stats?.session
  const throughput = stats?.throughput ?? []
  const composition = stats?.composition ?? []
  const peak = Math.max(1, ...throughput.map((day) => day.count))
  const largest = Math.max(1, ...composition.map((band) => band.count))
  const mean = throughput.length
    ? Math.round(throughput.reduce((sum, day) => sum + day.count, 0) / throughput.length)
    : 0

  const rows: [string, string][] = [
    ['Reviewed', String(session?.reviewed ?? 0)],
    ['Accepted as-is', String(session?.accepted ?? 0)],
    ['Corrected', String(session?.corrected ?? 0)],
    ['Skipped', String(session?.skipped ?? 0)],
    ['Marked illegible', String(session?.illegible ?? 0)],
    ['Mean time', session?.mean_seconds ? `${session.mean_seconds.toFixed(1)} s` : 'none yet'],
    ['Rate', session?.rate_per_min ? `${session.rate_per_min.toFixed(1)} / min` : 'none yet'],
    ['Streak', String(session?.streak ?? 0)],
  ]

  return (
    <div className="w-[252px] flex-none overflow-y-auto border-l border-n300 bg-surface px-[15px] py-3.5">
      <div className="chrome mb-[11px] text-[11px]">This session</div>
      <div className="mb-[18px] flex flex-col gap-[9px]">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-baseline justify-between gap-2.5 border-b border-n300 pb-[7px]"
          >
            <div className="text-[13.5px] text-ink2">{label}</div>
            <div className="font-mono text-[13px]">{value}</div>
          </div>
        ))}
      </div>

      <div className="chrome mb-[11px] text-[11px]">Throughput, last 14 days</div>
      {throughput.length ? (
        <>
          <div className="mb-1.5 flex h-[54px] items-end gap-[2px]">
            {throughput.map((day, index) => {
              const last = index === throughput.length - 1
              return (
                <div
                  key={day.date}
                  title={`${day.date}: ${day.count}`}
                  className="flex-1"
                  style={{
                    height: day.count ? `${Math.max(4, (day.count / peak) * 100)}%` : '3px',
                    background: day.count ? 'var(--jl-accent)' : 'var(--color-neutral-300)',
                    opacity: last ? 1 : day.count ? 0.45 : 1,
                  }}
                />
              )
            })}
          </div>
          <div className="mb-[18px] font-mono text-[9.5px] uppercase text-ink2">
            Mean {mean}/day · every editor
          </div>
        </>
      ) : (
        <p className="mb-[18px] text-[13.5px] text-ink2">
          Nothing has been decided in the last fortnight, so there is no throughput to draw.
        </p>
      )}

      <div className="chrome mb-[11px] text-[11px]">Queue composition</div>
      {composition.length ? (
        <div className="flex flex-col gap-2">
          {composition.map((band, index) => (
            <div key={band.label} className="flex items-center gap-[9px]">
              <div className="w-[66px] flex-none font-mono text-[10.5px] text-ink2">
                {band.label}
              </div>
              <div className="h-2 flex-1 overflow-hidden bg-n300">
                <div
                  className="h-full"
                  style={{
                    width: `${(band.count / largest) * 100}%`,
                    background: index === 0 ? 'var(--color-accent-2-700)' : 'var(--jl-accent)',
                  }}
                />
              </div>
              <div className="w-[30px] flex-none text-right font-mono text-[10px]">
                {band.count}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[13.5px] text-ink2">The queue is empty, so it has no composition.</p>
      )}

      <div className="mt-[18px] border-t border-n300 pt-3.5 font-mono text-[9.5px] uppercase leading-[1.75] text-ink2">
        <div className="text-ink">Every decision is attributed</div>
        <div>raw_text is never overwritten</div>
        <div>Corrections land in corrected_text</div>
      </div>
    </div>
  )
}
